import React, { useRef, useCallback } from 'react'
import './animations.css'

export interface TutorialOverlayProps {
  visible: boolean
  // Retângulo do alvo a destacar
  targetRect: DOMRect | null
  onBackdropClick?: () => void
  // Quando true, bloqueia cliques fora do alvo (hard gate)
  restrictOutsideClicks?: boolean
}

// Overlay com spotlight real: quando há alvo, escurece apenas ao redor e mantém o interior claro
const TutorialOverlay: React.FC<TutorialOverlayProps> = ({ visible, targetRect, onBackdropClick, restrictOutsideClicks = false }) => {

  // ✅ iOS: Obter dimensões do visualViewport quando disponível (para compatibilidade com teclado virtual)
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight
  const viewportWidth = window.visualViewport?.width ?? window.innerWidth

  // Normaliza retângulo para evitar artefatos de subpixel (linhas finas)
  // ✅ iOS: getBoundingClientRect() já retorna coordenadas corretas relativas ao viewport
  // Não é necessário compensar offset - os listeners do Provider já recalculam targetRect
  const r = targetRect
    ? (() => {
        const left = Math.floor(targetRect.left)
        const top = Math.floor(targetRect.top)
        const right = Math.ceil(targetRect.right)
        const bottom = Math.ceil(targetRect.bottom)
        const width = Math.max(0, right - left)
        const height = Math.max(0, bottom - top)
        return { left, top, right, bottom, width, height }
      })()
    : null

  // Encaminhar rolagem (wheel/touch) para o elemento subjacente sob o ponteiro
  const forwardWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!restrictOutsideClicks) return
    e.preventDefault()
    const blocker = e.currentTarget as HTMLElement
    const { clientX, clientY, deltaY } = e
    // Temporariamente desabilita pointer-events para identificar o elemento subjacente
    const prev = blocker.style.pointerEvents
    blocker.style.pointerEvents = 'none'
    const under = document.elementFromPoint(clientX, clientY) as HTMLElement | null
    blocker.style.pointerEvents = prev
    // Procura ancestral rolável
    let scroller: HTMLElement | null = under
    const isScrollable = (el: HTMLElement) => {
      const cs = window.getComputedStyle(el)
      const canScroll = (cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollHeight > el.clientHeight
      return canScroll
    }
    while (scroller && scroller !== document.body && !isScrollable(scroller)) {
      scroller = scroller.parentElement
    }
    if (scroller && isScrollable(scroller)) {
      scroller.scrollTop += deltaY
    } else {
      window.scrollBy({ top: deltaY })
    }
  }, [restrictOutsideClicks])

  const touchStartY = useRef<number | null>(null)
  const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!restrictOutsideClicks) return
    touchStartY.current = e.touches[0]?.clientY ?? null
  }, [restrictOutsideClicks])

  const onTouchMove = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!restrictOutsideClicks) return
    if (touchStartY.current == null) return
    const currentY = e.touches[0]?.clientY ?? touchStartY.current
    const deltaY = touchStartY.current - currentY
    e.preventDefault()
    const blocker = e.currentTarget as HTMLElement
    const touch = e.touches[0]
    const prev = blocker.style.pointerEvents
    blocker.style.pointerEvents = 'none'
    const under = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null
    blocker.style.pointerEvents = prev
    let scroller: HTMLElement | null = under
    const isScrollable = (el: HTMLElement) => {
      const cs = window.getComputedStyle(el)
      const canScroll = (cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollHeight > el.clientHeight
      return canScroll
    }
    while (scroller && scroller !== document.body && !isScrollable(scroller)) {
      scroller = scroller.parentElement
    }
    if (scroller && isScrollable(scroller)) {
      scroller.scrollTop += deltaY
    } else {
      window.scrollBy({ top: deltaY })
    }
  }, [restrictOutsideClicks])

  if (!visible) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        // Quando há alvo, o fundo do container fica transparente e
        // usamos 4 blocos para escurecer apenas ao redor. Sem alvo,
        // escurecemos tudo para manter comportamento previsível.
        backgroundColor: targetRect ? 'transparent' : 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        // Importante: manter 'none' para permitir clique no alvo; se não houver alvo e for hard, bloquear toda a tela
        pointerEvents: restrictOutsideClicks && !targetRect ? 'auto' : 'none',
      }}
      onClick={onBackdropClick}
      aria-modal="true"
      role="dialog"
      aria-label="Guided Tutorial"
      aria-describedby="tutorial-bubble"
    >
      {r && (
        <>
          {/* Visual: escurecer fora do alvo com uma única máscara sem costuras */}
          <div
            style={{
              position: 'absolute',
              left: `${r.left}px`,
              top: `${r.top}px`,
              width: `${r.width}px`,
              height: `${r.height}px`,
              borderRadius: 8,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          />
          {/* Topo */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: `${Math.max(0, r.top)}px`,
              backgroundColor: 'transparent',
              pointerEvents: restrictOutsideClicks ? 'auto' : 'none',
            }}
            onWheel={forwardWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            aria-hidden="true"
          />
          {/* Base */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: `${r.bottom}px`,
              width: '100%',
              height: `${Math.max(0, viewportHeight - r.bottom)}px`,
              backgroundColor: 'transparent',
              pointerEvents: restrictOutsideClicks ? 'auto' : 'none',
            }}
            onWheel={forwardWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            aria-hidden="true"
          />
          {/* Esquerda */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: `${r.top}px`,
              width: `${Math.max(0, r.left)}px`,
              height: `${r.height}px`,
              backgroundColor: 'transparent',
              pointerEvents: restrictOutsideClicks ? 'auto' : 'none',
            }}
            onWheel={forwardWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            aria-hidden="true"
          />
          {/* Direita */}
          <div
            style={{
              position: 'absolute',
              left: `${r.right}px`,
              top: `${r.top}px`,
              width: `${Math.max(0, viewportWidth - r.right)}px`,
              height: `${r.height}px`,
              backgroundColor: 'transparent',
              pointerEvents: restrictOutsideClicks ? 'auto' : 'none',
            }}
            onWheel={forwardWheel}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            aria-hidden="true"
          />
          {/* Borda iluminada ao redor do alvo com glow animado */}
          <div
            style={{
              position: 'absolute',
              left: `${r.left - 8}px`,
              top: `${r.top - 8}px`,
              width: `${r.width + 16}px`,
              height: `${r.height + 16}px`,
              border: '2px solid #fff',
              borderRadius: 8,
              boxShadow: '0 0 0 6px rgba(255,255,255,0.25), 0 0 20px 4px rgba(139,92,246,0.4)',
              animation: 'pulseGlow 2s ease-in-out infinite',
              pointerEvents: 'none',
            }}
            aria-hidden="true"
          />
        </>
      )}
    </div>
  )
}

export default TutorialOverlay
